import type {
  AppRoute,
  GroupTab,
  PathRedirect,
  RouteTab,
  TabGroup,
  TabGroupFactory,
  TabGroupScope,
} from "@/@types/tab-navigation";
import { TabbedRouteGroup } from "@/components/tabbed-route-group";
import type { RoleEnum } from "@/enum/role";

import { createRoute } from "./createRoute";

const UNKNOWN_TAB_MESSAGE: string = "Aba inexistente no agrupamento";

const unionOfTabRoles = (tabs: GroupTab[]): RoleEnum[] => [
  ...new Set(tabs.flatMap((tab: GroupTab) => tab.requiredRole)),
];

export const createTabGroupFactory = (scope: TabGroupScope): TabGroupFactory => {
  const basePath = (group: TabGroup): string => `${scope.pathId}${group.path}`;

  const toRouteTab = (group: TabGroup, tab: GroupTab): RouteTab => ({
    requiredRole: tab.requiredRole,
    groupRequired: tab.groupRequired,
    requiredAction: tab.requiredAction,
    label: tab.label,
    url: `${basePath(group)}/${tab.path}`,
  });

  const tabRedirects = (group: TabGroup): PathRedirect[] =>
    group.tabs.flatMap((tab: GroupTab): PathRedirect[] =>
      tab.previousPath ? [{ from: tab.previousPath, to: `${basePath(group)}/${tab.path}` }] : [],
    );

  return {
    createRoute: (group: TabGroup): AppRoute =>
      createRoute({
        id: group.label,
        category: group.category,
        requiredRole: unionOfTabRoles(group.tabs),
        groupRequired: scope.groupRequired,
        path: `${group.path}/*`,
        pathId: scope.pathId,
        element: <TabbedRouteGroup basePath={basePath(group)} tabs={group.tabs} />,
        Icon: group.Icon,
        tabs: group.tabs.map((tab: GroupTab): RouteTab => toRouteTab(group, tab)),
      }),

    buildRedirects: (groups: TabGroup[]): PathRedirect[] => groups.flatMap(tabRedirects),

    tabUrl: <K extends string>(group: TabGroup<K>, tabKey: NoInfer<K>): string => {
      const tab: GroupTab<K> | undefined = group.tabs.find((candidate: GroupTab<K>) => candidate.key === tabKey);
      if (!tab) throw new Error(`${UNKNOWN_TAB_MESSAGE}: ${group.label} › ${tabKey}`);

      return `${basePath(group)}/${tab.path}`;
    },
  };
};
